import { catColors } from "@/domain/categories";
import type { ResolvedLayer } from "@/domain/profile-resolver/index";
import type { BoardLayer, Category, Keyboard, KeyboardLayout, RenderKeycap } from "@/domain/types";
import { activeCodeOf, legendOf } from "@/hardware/keyboard-layouts/index";

/**
 * The lens: a resolved Layer x Keyboard x Keyboard Layout into renderable keycaps.
 * The layer carries provenance, passed through as sourceCombo; marks is
 * the set of codes the active combo decorates with a rapid-fire glyph.
 */
export function compose(
  layer: ResolvedLayer,
  keyboard: Keyboard,
  keyboardLayout: KeyboardLayout | BoardLayer,
  categories: Record<string, Category>,
  marks?: ReadonlySet<string> | null,
): RenderKeycap[] {
  // Positions are keyed uniformly by a key's ACTIVE (current-layer) keycode, and each cap shows
  // the legend its mapping declares for that position - both resolved by the one position
  // resolver, so this stays pure assembly: code + legend in, binding + colors out.
  return keyboard.keys.map((k, i) => {
    const code = activeCodeOf(keyboard, keyboardLayout, i);
    const resolved = code != null ? (layer[code] ?? null) : null;
    const binding = resolved?.binding ?? null;
    const legend = legendOf(keyboard, keyboardLayout, i);
    const colors = binding ? catColors(categories[binding.category]) : null;
    const cap: RenderKeycap = { ...k, code, legend, binding, colors };
    if (resolved) cap.sourceCombo = resolved.combo;
    // marks (the SC2 rapid-fire overlay) key by the cap's ACTIVE code, the one code space the table
    // and the projection now share; a remapped key's glyph and its binding stay together
    if (code != null && marks?.has(code)) cap.rapidFire = true;
    return cap;
  });
}
